<template>
  <Teleport to="body">
    <Transition name="slide-up">
      <DraggableWrapper
        v-if="tunerStore.isOpen && !tunerStore.isFullscreen"
        ref="wrapperRef"
        :x="pos.x"
        :y="pos.y"
        :handleOnly="true"
        positionType="fixed"
        :constrainToViewport="true"
        :dragZIndex="61"
        @update:position="savePos"
        class="z-[60] max-w-[calc(100vw-2rem)]"
      >
      <div
        class="relative flex items-center gap-2.5 rounded-2xl pl-2.5 pr-2.5 py-3 shadow-2xl select-none text-white transition-shadow"
        style="background-color: #1c2547"
        :class="[
          tunerState.inTune ? 'ring-2 ring-emerald-400 shadow-emerald-500/20' : '',
          !tunerState.isListening && (tunerState.errorKey || tunerState.error) ? 'ring-2 ring-rose-400' : '',
        ]"
        role="status"
        :aria-label="$t('tuner.tunerMinimized')"
        :title="!tunerState.isListening && tunerState.errorKey ? $t(`tuner.${tunerState.errorKey}`) : ''"
      >
        <!-- Alça de arraste (grip padrão do sistema) -->
        <span
          class="handle cursor-grab active:cursor-grabbing text-slate-500 hover:text-slate-300 shrink-0 touch-none"
          :title="$t('tuner.dragHandle')"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 6.75h.008v.008H8.25V6.75zm0 3h.008v.008H8.25V9.75zm0 3h.008v.008H8.25v-.008zm0 3h.008v.008H8.25v-.008zm3-9h.008v.008H11.25V6.75zm0 3h.008v.008H11.25V9.75zm0 3h.008v.008H11.25v-.008zm0 3h.008v.008H11.25v-.008zm3-9h.008v.008H14.25V6.75zm0 3h.008v.008H14.25V9.75zm0 3h.008v.008H14.25v-.008zm0 3h.008v.008H14.25v-.008z" />
          </svg>
        </span>
        <!-- Badge de afinação atingida -->
        <div
          v-if="tunerState.inTune"
          class="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap px-2.5 py-0.5 bg-emerald-500 text-white font-black text-[10px] uppercase tracking-wider rounded-full shadow-lg animate-pulse"
        >
          ✓ {{ $t('tuner.inTune') }}
        </div>
        <!-- Gráfico de barras do sinal (lado esquerdo) -->
        <div
          class="flex items-end gap-[3px] h-10"
          :title="`${$t('tuner.signal')} ${tunerState.sustain}%`"
        >
          <div
            v-for="(b, i) in signalBars"
            :key="i"
            class="w-[5px] rounded-full transition-all duration-150"
            :style="{
              height: `${b.height}px`,
              backgroundColor: b.active ? '#5ea3f7' : '#33415e'
            }"
          ></div>
        </div>

        <!-- Radial compacto (centro) -->
        <button
          type="button"
          @click="tunerStore.openFullscreen()"
          :title="$t('tuner.expandTitle')"
          class="hover:opacity-90 transition-opacity"
        >
          <TriadTunerDial :size="76" compact dark />
        </button>

        <!-- Ações: expandir + fechar -->
        <div class="flex flex-col items-center gap-0.5">
          <button
            type="button"
            @click="tunerStore.openFullscreen()"
            class="p-1.5 text-slate-300 hover:text-white rounded-lg hover:bg-white/10 transition-colors"
            :title="$t('tuner.expandTitle')"
          >
            <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
            </svg>
          </button>
          <button
            type="button"
            @click="closeTuner"
            class="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-white/10 transition-colors"
            :title="$t('tuner.close')"
          >
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
      </DraggableWrapper>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed, watch, onMounted, onUnmounted } from 'vue';
import { tunerService } from '@/service/audio/tuner.service';
import tunerStore from '@/store/tuner.store';
import { appEvents } from '@/store/app.events';
import TriadTunerDial from '@/components/audio/TriadTunerDial.vue';
import DraggableWrapper from '@/components/common/elements/DraggableWrapper.vue';
import { useFloatingMini } from '@/composables/useFloatingMini';

const { pos, wrapperRef, savePos } = useFloatingMini('mini-tuner-position', {
  estimate: { w: 250, h: 120 },
});

const tunerState = tunerService.state;

// 7 barras com variação fixa para parecer um equalizador vivo
const BAR_FACTORS = [0.45, 0.7, 1, 0.85, 0.6, 0.9, 0.5];

const signalBars = computed(() => {
  const level = Math.max(0, Math.min(100, tunerState.sustain || 0));
  return BAR_FACTORS.map((f) => {
    const h = 4 + Math.round((level / 100) * 36 * f);
    return { height: h, active: level > 4 && h > 6 };
  });
});

const closeTuner = () => {
  tunerService.stop();
  tunerStore.close();
};

// Global event listener — works from ANY route, not just PlaylistSongView
const handleToggle = () => {
  tunerStore.toggle();
};

onMounted(() => {
  appEvents.on('toggleTuner', handleToggle);
});

onUnmounted(() => {
  appEvents.off('toggleTuner', handleToggle);
  if (tunerState.isListening) {
    tunerService.stop();
  }
});

watch(
  () => tunerStore.isOpen,
  (open) => {
    if (open && !tunerState.isListening) {
      tunerService.start();
    } else if (!open && tunerState.isListening) {
      tunerService.stop();
    }
  }
);
</script>

<style scoped>
.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.25s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.25s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}
</style>
